import { useCallback, useState } from 'react';
import { Copy, Send } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { ScrapeButton } from './ScrapeButton';
import { GradeButton } from './GradeButton';
import { CREDIT_COSTS, FEATURE_FLAGS } from '@/services/api';
import {
  startLinkedIn360Scrape,
  pollLinkedIn360Results,
  startGlassdoorScrape,
  pollGlassdoorResults,
  startConsumerIntelScrape,
  pollConsumerIntelResults,
  startCrunchbaseScrape,
  pollCrunchbaseResults,
  pushToMake,
} from '@/services/scraping';
import { enqueueScrapeJob } from '@/services/scrapeQueue';
import {
  calculateInvestorIQ,
  calculateFundingIQ,
  calculateInvestorMap,
  addToLAL,
  removeFromLAL,
} from '@/services/grading';
import { deductCredits } from '@/services/credits';
import { getCredits } from '@/services/credits';
import { copyToClipboard } from '@/utils/helpers';
import type { Company, CompanyIntel, ScrapeSource } from '@/types';

export function InvestorCompanyTable() {
  const {
    userEmail,
    credits,
    filteredCompanies,
    recruiterIntelData,
    lalList,
    currentPage,
    perPage,
    setCredits,
    updateIntelData,
    toggleLAL,
    openScrapeModal,
    updateScrapeProgress,
    closeScrapeModal,
    openDrawer,
    openGradeDrawer,
  } = useAppStore();

  const [loadingGrades, setLoadingGrades] = useState<Record<string, string>>({});
  const [pushingCompanies, setPushingCompanies] = useState<Set<string>>(new Set());

  // Pagination
  const startIdx = (currentPage - 1) * perPage;
  const endIdx = startIdx + perPage;
  const displayedCompanies = filteredCompanies.slice(startIdx, endIdx);

  // Save intel to store (and IndexedDB via effect)
  const saveIntel = useCallback((companyId: string, source: ScrapeSource, data: unknown) => {
    const currentIntel = recruiterIntelData[companyId] || {};
    const newIntel: CompanyIntel = {
      ...currentIntel,
      [source]: {
        data,
        scrapedAt: new Date().toISOString(),
      },
    };
    updateIntelData(companyId, newIntel);
  }, [recruiterIntelData, updateIntelData]);

  // Handle scraping
  const handleScrape = useCallback(async (company: Company, source: ScrapeSource, forceRefresh = false) => {
    const cost = CREDIT_COSTS[source] || 2;
    if (credits < cost) {
      alert(`Insufficient credits. Need ${cost} credits.`);
      return;
    }

    openScrapeModal(source, company.companyName);

    try {
      // Deduct credits (skip when backend enforces credits/refunds)
      if (!FEATURE_FLAGS.SERVER_SIDE_CREDITS) {
        const newCredits = await deductCredits(userEmail, cost, `recruiter_${source}`);
        setCredits(newCredits);
      }

      updateScrapeProgress(10, 'Starting scrape...');

      updateScrapeProgress(15, 'Queued. Running in background...');
      setTimeout(closeScrapeModal, 800);

      enqueueScrapeJob(
        {
          source,
          companyName: company.companyName,
          label: `${company.companyName} • ${source}`,
        },
        async (update) => {
          let startResult: any;
          update(10, 'Starting scrape...');
          switch (source) {
            case 'linkedin_360':
              startResult = await startLinkedIn360Scrape(company.companyName, company.linkedin, userEmail, forceRefresh);
              if (!startResult.success) throw new Error(startResult.error || 'Failed to start LinkedIn scrape');
              update(20, 'Scraping LinkedIn...');
              saveIntel(company.companyId, source, await pollLinkedIn360Results(startResult, update));
              break;
            case 'glassdoor':
              startResult = await startGlassdoorScrape(company.companyName, userEmail, forceRefresh);
              if (!startResult.success) throw new Error(startResult.error || 'Failed to start Glassdoor scrape');
              update(20, 'Scraping Glassdoor...');
              saveIntel(company.companyId, source, await pollGlassdoorResults(startResult, update));
              break;
            case 'consumer_intel':
              startResult = await startConsumerIntelScrape(company.companyName, userEmail, forceRefresh);
              if (!startResult.success) throw new Error(startResult.error || 'Failed to start Consumer Intel scrape');
              update(20, 'Scraping Consumer Intel...');
              saveIntel(company.companyId, source, await pollConsumerIntelResults(startResult, update));
              break;
            case 'crunchbase':
              startResult = await startCrunchbaseScrape(company.companyName, userEmail, forceRefresh);
              if (!startResult.success) throw new Error(startResult.error || 'Failed to start Crunchbase scrape');
              update(20, 'Scraping Crunchbase...');
              saveIntel(company.companyId, source, await pollCrunchbaseResults(startResult, update));
              break;
          }

          try {
            const refreshed = await getCredits(userEmail);
            setCredits(refreshed);
          } catch {
            // ignore
          }
        }
      );
    } catch (error) {
      closeScrapeModal();
      alert(`Scrape failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      try {
        const refreshed = await getCredits(userEmail);
        setCredits(refreshed);
      } catch {
        // ignore
      }
    }
  }, [credits, userEmail, setCredits, openScrapeModal, updateScrapeProgress, closeScrapeModal, saveIntel]);

  // Handle grading
  const handleGrade = useCallback(async (company: Company, gradeType: string) => {
    const intel = recruiterIntelData[company.companyId];
    if (!intel) return;

    setLoadingGrades(prev => ({ ...prev, [`${company.companyId}-${gradeType}`]: 'loading' }));

    try {
      let gradeData;
      switch (gradeType) {
        case 'investoriq':
          gradeData = await calculateInvestorIQ(company.companyId, company.companyName, intel);
          break;
        case 'fundingiq':
          gradeData = await calculateFundingIQ(company.companyId, company.companyName, intel);
          break;
        case 'investormap':
          gradeData = await calculateInvestorMap(company.companyId, company.companyName, intel);
          break;
      }

      if (gradeData) {
        const currentIntel = recruiterIntelData[company.companyId] || {};
        const newIntel: CompanyIntel = {
          ...currentIntel,
          [gradeType]: {
            data: gradeData,
            gradedAt: new Date().toISOString(),
          },
        };
        updateIntelData(company.companyId, newIntel);
      }
    } catch (error) {
      alert(`Grade failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoadingGrades(prev => {
        const newState = { ...prev };
        delete newState[`${company.companyId}-${gradeType}`];
        return newState;
      });
    }
  }, [recruiterIntelData, updateIntelData]);

  // Handle LAL toggle
  const handleLALToggle = useCallback(async (company: Company) => {
    const isInLAL = lalList[company.companyId];
    
    try {
      if (isInLAL) {
        await removeFromLAL(userEmail, company.companyId);
      } else {
        const intel = recruiterIntelData[company.companyId];
        await addToLAL(userEmail, company.companyId, company.companyName, { company, intel });
      }
      toggleLAL(company.companyId);
    } catch (error) {
      console.error('LAL toggle failed:', error);
    }
  }, [userEmail, lalList, recruiterIntelData, toggleLAL]);

  // Handle push to Make
  const handlePush = useCallback(async (company: Company) => {
    setPushingCompanies(prev => new Set(prev).add(company.companyId));
    
    try {
      const intel = recruiterIntelData[company.companyId] || {};
      const success = await pushToMake(company, {
        linkedin_360: intel.linkedin_360?.data,
        glassdoor: intel.glassdoor?.data,
        consumer_intel: intel.consumer_intel?.data,
        crunchbase: intel.crunchbase?.data,
        paygrade: intel.paygrade?.data,
        interviewiq: intel.interviewiq?.data,
        investoriq: intel.investoriq?.data,
        fundingiq: intel.fundingiq?.data,
        pushedBy: userEmail,
      });
      
      if (!success) throw new Error('Webhook failed');
    } catch (error) {
      alert(`Push failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTimeout(() => {
        setPushingCompanies(prev => {
          const newSet = new Set(prev);
          newSet.delete(company.companyId);
          return newSet;
        });
      }, 2000);
    }
  }, [userEmail, recruiterIntelData]);

  // Get scrape summary for display
  const getScrapeSummary = (intel: CompanyIntel | undefined, source: ScrapeSource): string | undefined => {
    if (!intel?.[source]?.data) return undefined;
    const data = intel[source]!.data;

    switch (source) {
      case 'linkedin_360':
        return `${(data as { jobs?: unknown[] }).jobs?.length || 0} jobs`;
      case 'glassdoor': {
        const gd = data as { overview?: Array<{ rating?: number }> };
        const rating = gd.overview?.[0]?.rating;
        return rating ? `⭐${rating}` : '✓';
      }
      case 'consumer_intel': {
        const ci = data as { trustpilot?: Array<{ score?: number }>; google?: Array<{ rating?: number }> };
        const parts = [];
        if (ci.trustpilot?.[0]?.score) parts.push(`TP:${ci.trustpilot[0].score}`);
        if (ci.google?.[0]?.rating) parts.push(`G:${ci.google[0].rating}`);
        return parts.length > 0 ? parts.join(' ') : '✓';
      }
      default:
        return '✓';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10">
          <tr className="border-b border-gray-700">
            <th className="px-2 py-3 text-center text-xs font-semibold text-gray-400 uppercase bg-card-dark">Watch</th>
            <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Company</th>
            <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Contact</th>
            <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Phone</th>
            <th className="px-2 py-3 text-left text-xs font-semibold text-gray-400 uppercase bg-card-dark">Email</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-blue-300 uppercase bg-card-dark" title="Company page, posts, and job listings">LinkedIn 360</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-green-300 uppercase bg-card-dark" title="Employee sentiment, growth signals">Glassdoor 360</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-yellow-300 uppercase bg-card-dark" title="Trustpilot, G2 - product market fit signals">Consumer Intel</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-purple-300 uppercase bg-card-dark" title="Funding, leadership, competitors">Crunchbase</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-amber-300 uppercase bg-card-dark" title="AI-graded: Overall investment attractiveness">InvestorIQ</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-rose-300 uppercase bg-card-dark" title="AI-graded: Financial stability and runway">FundingIQ</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-teal-300 uppercase bg-card-dark" title="AI-graded: Best funding types for this company">InvestorMap</th>
            <th className="px-2 py-3 text-center text-xs font-semibold text-pink-300 uppercase bg-card-dark" title="Push to Make.com">Push</th>
          </tr>
        </thead>
        <tbody>
          {displayedCompanies.map((company) => {
            const intel = recruiterIntelData[company.companyId];
            const contact = company.contacts[0] || { name: '', title: '', phone: '', email: '' };
            const isPushing = pushingCompanies.has(company.companyId);

            return (
              <tr key={company.companyId} className="border-b border-gray-800 hover:bg-gray-900/50">
                {/* Watch Checkbox */}
                <td className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={!!lalList[company.companyId]}
                    onChange={() => handleLALToggle(company)}
                    className="w-4 h-4 accent-amber-500 cursor-pointer"
                    title="Add to Watch List"
                  />
                </td>

                {/* Company */}
                <td className="px-2 py-3">
                  <div className="font-semibold text-white text-sm">{company.companyName}</div>
                  <div className="text-xs text-gray-500">{company.contacts.length} contacts</div>
                </td>

                {/* Contact */}
                <td className="px-2 py-3">
                  <div className="text-sm text-white">{contact.name}</div>
                  <div className="text-xs text-gray-400">{contact.title}</div>
                </td>

                {/* Phone */}
                <td className="px-2 py-3">
                  {company.phone ? (
                    <button
                      onClick={() => copyToClipboard(company.phone)}
                      className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                    >
                      {company.phone} <Copy className="w-3 h-3" />
                    </button>
                  ) : (
                    <span className="text-gray-600">-</span>
                  )}
                </td>

                {/* Email */}
                <td className="px-2 py-3">
                  {company.email ? (
                    <button
                      onClick={() => copyToClipboard(company.email)}
                      className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                    >
                      {company.email.substring(0, 25)}{company.email.length > 25 ? '...' : ''} <Copy className="w-3 h-3" />
                    </button>
                  ) : (
                    <span className="text-gray-600">-</span>
                  )}
                </td>

                {/* LinkedIn 360 */}
                <td className="px-2 py-3 text-center">
                  <ScrapeButton
                    hasData={!!intel?.linkedin_360}
                    summary={getScrapeSummary(intel, 'linkedin_360')}
                    creditCost={CREDIT_COSTS.linkedin_360}
                    onScrape={() => handleScrape(company, 'linkedin_360')}
                    onRescrape={() => handleScrape(company, 'linkedin_360', true)}
                    onViewData={() => openDrawer(company.companyId, company.companyName, 'linkedin_360')}
                    unavailable={!company.linkedin}
                    unavailableReason="No LinkedIn URL"
                  />
                </td>

                {/* Glassdoor */}
                <td className="px-2 py-3 text-center">
                  <ScrapeButton
                    hasData={!!intel?.glassdoor}
                    summary={getScrapeSummary(intel, 'glassdoor')}
                    creditCost={CREDIT_COSTS.glassdoor}
                    onScrape={() => handleScrape(company, 'glassdoor')}
                    onRescrape={() => handleScrape(company, 'glassdoor', true)}
                    onViewData={() => openDrawer(company.companyId, company.companyName, 'glassdoor')}
                  />
                </td>

                {/* Consumer Intel */}
                <td className="px-2 py-3 text-center">
                  <ScrapeButton
                    hasData={!!intel?.consumer_intel}
                    summary={getScrapeSummary(intel, 'consumer_intel')}
                    creditCost={CREDIT_COSTS.consumer_intel}
                    onScrape={() => handleScrape(company, 'consumer_intel')}
                    onRescrape={() => handleScrape(company, 'consumer_intel', true)}
                    onViewData={() => openDrawer(company.companyId, company.companyName, 'consumer_intel')}
                  />
                </td>

                {/* Crunchbase */}
                <td className="px-2 py-3 text-center">
                  <ScrapeButton
                    hasData={!!intel?.crunchbase}
                    summary={getScrapeSummary(intel, 'crunchbase')}
                    creditCost={CREDIT_COSTS.crunchbase}
                    onScrape={() => handleScrape(company, 'crunchbase')}
                    onRescrape={() => handleScrape(company, 'crunchbase', true)}
                    onViewData={() => openDrawer(company.companyId, company.companyName, 'crunchbase')}
                  />
                </td>

                {/* InvestorIQ */}
                <td className="px-2 py-3 text-center">
                  <GradeButton
                    grade={intel?.investoriq?.data?.overall_grade || null}
                    onGrade={() => handleGrade(company, 'investoriq')}
                    onRegrade={() => handleGrade(company, 'investoriq')}
                    onViewGrade={() => openGradeDrawer(company.companyId, company.companyName, 'investoriq')}
                    hasRequiredData={!!intel?.crunchbase}
                    requiredDataMessage="Scrape Crunchbase first"
                    isLoading={!!loadingGrades[`${company.companyId}-investoriq`]}
                    colorScheme="amber"
                  />
                </td>

                {/* FundingIQ */}
                <td className="px-2 py-3 text-center">
                  <GradeButton
                    grade={intel?.fundingiq?.data?.overall_grade || null}
                    onGrade={() => handleGrade(company, 'fundingiq')}
                    onRegrade={() => handleGrade(company, 'fundingiq')}
                    onViewGrade={() => openGradeDrawer(company.companyId, company.companyName, 'fundingiq')}
                    hasRequiredData={!!intel?.crunchbase}
                    requiredDataMessage="Scrape Crunchbase first"
                    isLoading={!!loadingGrades[`${company.companyId}-fundingiq`]}
                    colorScheme="rose"
                  />
                </td>

                {/* InvestorMap */}
                <td className="px-2 py-3 text-center">
                  <GradeButton
                    grade={intel?.investormap?.data?.best_fit?.substring(0, 8) || intel?.investormap?.data?.overall_grade || null}
                    onGrade={() => handleGrade(company, 'investormap')}
                    onRegrade={() => handleGrade(company, 'investormap')}
                    onViewGrade={() => openGradeDrawer(company.companyId, company.companyName, 'investormap')}
                    hasRequiredData={!!intel?.crunchbase}
                    requiredDataMessage="Scrape Crunchbase first"
                    isLoading={!!loadingGrades[`${company.companyId}-investormap`]}
                    colorScheme="teal"
                    label="Map"
                  />
                </td>

                {/* Push to Make */}
                <td className="px-2 py-3 text-center">
                  <button
                    onClick={() => handlePush(company)}
                    disabled={isPushing}
                    className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                      isPushing
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-br from-pink-500 to-pink-700 hover:from-pink-400 hover:to-pink-600 text-white'
                    }`}
                    title="Push all data to Make.com"
                  >
                    {isPushing ? '✓' : <Send className="w-3 h-3" />}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
